// # REST 클라이언트 — 진실을 가져오는 유일한 문
//
// 소켓은 알림이고 REST 가 진실이다(`sync.ts`). 그 진실을 가져오는 호출은 전부
// 여기를 지난다. 흩어지면 헤더를 빠뜨린 `fetch` 하나가 언젠가 401 을 만든다.
//
// ## 토큰은 함수로 받는다
// 값으로 받으면 객체 안에 눌러앉아 갱신이 안 된다. 함수로 받으면 부를 때마다
// 최신 것을 얻고, 어디에 두는지(메모리·금고)는 플랫폼이 정한다. 이 파일은 모른다.
//
// ## 오류는 상태 코드째 던진다
// 429 는 `Retry-After` 를 따라 기다리고, 401 은 다시 로그인이고, 404 는 "없거나 못
// 보는 것"(서버는 둘을 구분해 주지 않는다 — 존재 여부가 새면 안 되므로)이다.
// 부르는 쪽이 이 셋을 다르게 다뤄야 하니 숫자를 잃지 않는다.

import type {
  AppEventType,
  Block,
  ChannelId,
  ClientMsgId,
  GroupId,
  Seq,
  Span,
  SsoMode,
  UserId,
  View,
} from '@maru/protocol'
import { CHANNEL_PAGE_MAX, MEMBER_PAGE_MAX } from '@maru/protocol'
import type { TotpSetup } from './auth.ts'

/** 내보내기 요청 한 건. */
/**
 * 창을 낸 결과(`POST /v1/views/{id}/submit`). 앱의 답이 고른다 — 이 창 하나 닫힘(아래
 * 창이 있으면 그것이 다시 보인다), 더미 전부 닫힘, 칸별 오류, 같은 자리에서 바뀐 창,
 * 이 창 위에 얹힌 새 창.
 */
export type ViewOutcome =
  | { outcome: 'closed' }
  | { outcome: 'cleared' }
  | { outcome: 'errors'; errors: Record<string, string> }
  | { outcome: 'updated'; view: View; hash: string }
  | { outcome: 'pushed'; id: number; view: View }

/** 앱의 사건 구독(슬랙 Events API). 주소 없이 소켓만 쓸 수 있다. */
export interface AppEventSub {
  url: string | null
  events: AppEventType[]
  socket: boolean
  /** 연달아 실패해 서버가 멈췄다. `app_events_resume` 으로 다시 켠다. */
  paused: boolean
  /** 적은 직후 한 번만 온다. */
  signing_secret?: string
}

/** 앱의 단축 하나(슬랙 shortcuts). 전역은 작성칸 곁, 글은 글의 손에 뜬다. */
export interface Shortcut {
  kind: 'global' | 'message'
  name: string
  description: string
  callback_id: string
}

/** 메뉴의 한 줄 — 어느 앱의 것인가가 붙는다. */
export interface ShortcutEntry extends Shortcut {
  app: string
  app_name: string
}

/** 감사 로그 한 줄. `action` 은 옛 줄의 모르는 이름일 수도 있어 글자다. */
export interface AuditRow {
  id: number
  at: string
  actor: number | null
  action: string
  entity: { type: string; id?: string }
  detail: string | null
  /** 로그인 줄의 출처. 요청이 주장한 값이라 적기만 한다. */
  ip?: string
  ua?: string
}

export interface AuditQuery {
  before?: number
  action?: string
  actor?: number
  limit?: number
}

export interface ExportJob {
  id: number
  state: 'pending' | 'done' | 'failed'
  requested_by: number
  bytes: number | null
  error: string | null
  created_at: string
  done_at: string | null
}

/** 보기 설정. 이름은 러스트 `domain::appearance` 의 와이어 이름 그대로다. */
export interface Appearance {
  theme: 'maru' | 'aubergine' | 'jade' | 'lagoon' | 'clementine' | 'mono'
  accent: string | null
  locale: 'ko' | 'en'
}

export type ChannelKind = 'public' | 'private' | 'dm' | 'group_dm'

export interface Channel {
  id: ChannelId
  kind: ChannelKind
  name: string | null
  topic: string | null
  last: Seq
  stored: boolean
  /** 공지 채널 — 새 글은 관리자·봇만, 답글은 누구나. 옛 서버는 안 보낸다. */
  announce?: boolean
  /** 내가 닫아 둔 대화방이면 닫을 때의 마지막 seq. `last` 가 넘으면 다시 보인다. 옛 서버는 안 보낸다. */
  closed_upto?: Seq
  /** 대화방이면 그 방의 사람들(나 포함). 사이드바가 상대 이름을 짓는 재료다. 옛 서버는 안 보낸다. */
  people?: UserId[]
}

/** 바깥으로 나온 프로필 한 줄. 평평하다 — 서버가 그 모양으로 준다. */
export interface ProfileRow {
  user: UserId
  title: string | null
  timezone: string | null
  status_emoji: string | null
  status_text: string | null
  status_until: string | null
  dnd_from: number | null
  dnd_to: number | null
  /** 하루 내내 조용한 요일(비트 0 = 월). */
  dnd_days: number
  /** 알림 멈춤의 끝. 멈추지 않았으면 `null`. */
  snooze_until: string | null
  /** 자리 비움으로 뒀다. 옛 서버는 싣지 않는다. */
  away?: boolean
}

/**
 * 프로필 고치기. **없는 열쇠는 안 건드리고, `null` 은 지운다.**
 * 상태는 세 칸이 한 덩어리다 — 평평하게 펴면 반쯤 고쳐진 상태가 생긴다.
 */
export interface ProfilePatch {
  title?: string | null
  timezone?: string | null
  status?: { emoji: string; text: string; until?: string | null } | null
  dnd?: { from: number; to: number; days?: number } | null
  /** 알림 멈춤의 끝(RFC 3339). `null` 이면 다시 켠다. */
  snooze_until?: string | null
  /** 자리 비움 켜고 끄기. */
  away?: boolean
}

/** 내가 낀 스레드 한 줄. 답글 본문은 열어서 읽는다. */
export interface ThreadBrief {
  channel: ChannelId
  root: Message
  replies: number
  last_at: string
}

/** 걸어 둔 예약 한 건. 아직 안 나간 것만 온다. */
export interface Scheduled {
  id: number
  channel: ChannelId
  parent: Seq | null
  blocks: Block[]
  /** 보낼 때. RFC 3339. */
  due_at: string
  /** 보내려다 실패한 마지막 이유. 있으면 화면에 그대로 보인다. */
  failed_why: string | null
}

/** 나를 향한 일 하나. 좌표와 그 글의 앞 140자(서식 없는 글자) — 지운 글은 애초에 오지 않는다. */
export interface Activity {
  kind: 'mention' | 'reaction' | 'thread_reply' | 'reminder'
  channel: ChannelId
  seq: Seq
  actor: UserId
  emoji: string | null
  at: string
  text: string
  /** 같은 앞부분의 조각(멘션·링크·이모지 포함). 옛 서버는 싣지 않는다. */
  preview?: Span[]
}

/**
 * 표시된 줄 하나. `by` 는 핀을 꽂은 사람 — 나중에 보기에는 없다(내 것이니까).
 */
export interface Marked {
  channel: ChannelId
  by: UserId | null
  message: Message
  /** 나중에 보기에만 있다. 러스트 `mark::Later`. */
  later?: Later
}

/** 나중에 보기의 탭. 러스트 `mark::LaterState`(snake_case). */
export type LaterState = 'in_progress' | 'archived' | 'completed'

export interface Later {
  state: LaterState
  /** RFC 3339. 없으면 `null`. */
  remind_at: string | null
}

/** 담아 둔 줄에 고칠 것. 빠진 열쇠는 그대로, `remind_at: null` 은 기한 지우기다. */
export interface LaterPatch {
  state?: LaterState
  remind_at?: string | null
}

/** 이 채널에서 무엇을 알릴 것인가. */
export type Notify = 'all' | 'mentions' | 'none'

/** 채널별 취향. **기본값인 채널은 목록에 없다** — 없으면 기본값이다. */
export interface ChannelPref {
  channel: ChannelId
  notify: Notify
  favorite: boolean
  section: string | null
}

/** 사이드바 칸 하나(슬랙 custom sections). 러스트 `prefs::SidebarSection`. */
export interface SidebarSection {
  name: string
  emoji?: string | null
  collapsed?: boolean
}

/** 취향에 고칠 것. `section: null` 은 "칸에서 빼라" 다. */
export interface PrefPatch {
  notify?: Notify
  favorite?: boolean
  section?: string | null
}

/** 채널에 고칠 것. 빠진 열쇠는 그대로 두고, `topic: null` 은 비운다. */
export interface ChannelPatch {
  name?: string
  topic?: string | null
  stored?: boolean
  announce?: boolean
  /** 공개↔비공개 전환. 관리자만. */
  kind?: 'public' | 'private'
}

/** 검색 물음. `before` 는 앞 쪽 마지막 줄의 `written_at` 그대로 — 커서다. */
/** 내 2단계 인증. `required` 면 속한 조직 하나가 요구하므로 끌 수 없다. */
export interface TwoFactorStatus {
  enabled: boolean
  backups_left: number
  required: boolean
}

/** 조직 보안 정책. `session_days` 가 `null` 이면 리프레시 수명(30일, 쓰면 연장)을 따른다. */
/** 조직의 SSO 설정. 비밀은 없다. `redirect_uri` 는 IdP 에 적어야 하는 돌아올 주소다. */
export interface SsoConfig {
  issuer: string
  client_id: string
  mode: SsoMode
  redirect_uri: string
}

/** 설정이 없어도 돌아올 주소는 온다 — IdP 에 앱을 먼저 등록할 수 있게. */
export interface SsoShown {
  sso: SsoConfig | null
  redirect_uri: string
}

export interface SsoInput {
  issuer: string
  client_id: string
  client_secret?: string
  mode: SsoMode
}

/** SCIM 토큰 한 줄. 원본은 만들 때 한 번만 온다. */
export interface ScimToken {
  id: number
  label: string
  created_by: UserId
  created_at: string
  last_used_at: string | null
}

export interface SecurityPolicy {
  require_2fa: boolean
  session_days: number | null
}

export interface SearchAsk {
  text: string
  channel?: ChannelId
  author?: UserId
  before?: string
  /** 이 시각 이후(포함), RFC 3339. `after:`·`on:`·`during:` 이 푼 값. */
  after?: string
  /** 이 시각 이전, RFC 3339. `before:` 가 푼 값 — 커서(`before`)와 따로 간다. */
  until?: string
  count?: number
  /** `is:thread`. */
  thread?: boolean
  /** `has:pin`. */
  pinned?: boolean
  /** `is:saved`. */
  saved?: boolean
  /** `has::eyes:` 의 이모지. */
  reaction?: string
  /** `hasmy::eyes:` — 내가 단 반응만. */
  reaction_mine?: boolean
  /** `with:@사람`. */
  with?: UserId
  /** `-in:#채널`. */
  not_channel?: ChannelId
  /** `-from:@사람`. */
  not_author?: UserId
}

export interface Hit {
  channel: ChannelId
  message: Message
}

/** 파일 탭의 조건. 메시지 검색의 수정자 중 채널·사람·기간만 뜻이 있다. */
export type FileAsk = Pick<
  SearchAsk,
  'text' | 'channel' | 'author' | 'before' | 'after' | 'until' | 'count'
>

/** 파일 결과 한 줄. `seq` 는 그 파일을 실은 글이다 — 누르면 그 줄로 뛴다. */
export interface FileHit {
  attachment: Attachment
  seq: Seq
}

/** AI 요약·검색 답 하나. 글의 `[n]` 은 `lines[n-1]` 의 좌표다. 나에게만 온다. */
export interface AiDigest {
  text: string
  /** 답에 실제로 달린 근거, 먼저 달린 순서. */
  sources: { channel: ChannelId; seq: Seq }[]
  /** 모델에게 넘긴 줄, `[1]` 부터. */
  lines: { channel: ChannelId; seq: Seq }[]
}

/** 허들 입장권. 화면은 이것으로 SFU(LiveKit)에 붙는다 — 처음 붙을 때만 쓰이고 금방 죽는다. */
export interface HuddleTicket {
  url: string
  token: string
  room: string
}

/** 열린 허들 하나. 사람은 들어온 순서. */
export interface HuddleLive {
  channel: ChannelId
  people: UserId[]
}

/** 무엇을 요약하는가 — 셋 중 하나. */
export type SummaryOf = { thread: Seq } | { unread: true } | { days: number }

// ── 백오피스·계정 ──────────────────────────────────────────────────────

export interface Invite {
  id: string
  email: string
  role: Role
  expires_at: string
}

/** 살아 있는 공유 초대 링크 한 줄(G30). 링크 주소는 없다 — 만들 때 한 번만 온다. */
export interface InviteLink {
  id: string
  creator: string
  created_at: string
  expires_at: string
  uses: number
  max_uses: number
}

/** 막 만든 링크. `url` 이 실리는 유일한 응답이다. */
export interface InviteLinkMade {
  id: string
  url: string
  expires_at: string
  max_uses: number
}

export interface Device {
  lineage: string
  device: string | null
  created_at: string
  last_written_at: string
  /** 접근 토큰만 든 요청은 자기 계보를 모른다 — 서버가 늘 거짓을 준다. */
  is_this: boolean
}

export interface Webhook {
  id: string
  channel: ChannelId
  bot: UserId
  name: string
  revoked: boolean
}

export interface OutgoingHook {
  id: string
  channel: ChannelId
  name: string
  url: string
  revoked: boolean
}

/** 워크플로의 트리거. 말은 소문자로 다듬어져 돌아온다. */
/** 일정의 주기. 평일은 만든 사람의 시간대(`offset_min`)로 가른다. */
export type WorkflowEvery = 'once' | 'daily' | 'weekdays' | 'weekly'

export type WorkflowTrigger =
  | { kind: 'keyword'; phrase: string }
  | { kind: 'joined' }
  /** `at` 은 첫 시각(RFC 3339), `offset_min` 은 만든 사람의 UTC 차이(분). */
  | { kind: 'schedule'; at: string; every: WorkflowEvery; offset_min: number }
  /** `POST /v1/workflow-hooks/{token}` 몸통의 글자 변수 이름. 답의 `{이름}` 을 채운다. */
  | { kind: 'webhook'; vars: string[] }

/** 워크플로 한 줄. 답은 `bot` 이 쓴다 — 걸 때 새로 판 봇이다. */
export interface Workflow {
  id: number
  channel: ChannelId
  name: string
  trigger: WorkflowTrigger
  reply: string
  in_thread: boolean
  bot: UserId
  enabled: boolean
  by: UserId
  /** 일정이 다음에 도는 시각. 일정이 아니거나 한 번짜리가 돌고 나면 `null`. */
  next_at: string | null
}

/** 만든 응답. 웹훅 트리거면 토큰이 **여기에만** 실린다. */
export type WorkflowCreated = Workflow & { token?: string }

export interface WorkflowDraft {
  channel: ChannelId
  name: string
  trigger: WorkflowTrigger
  /** `{user}` 는 트리거를 건드린 사람의 멘션이 된다. */
  reply: string
  in_thread: boolean
}

/** 사용자 그룹 한 줄. `handle` 이 `@` 뒤에 오는 그 이름이다. */
export interface UserGroup {
  id: GroupId
  handle: string
  name: string
  note: string | null
  members: UserId[]
}

/** 리스트의 열. `key` 는 칸 지도의 열쇠다. */
export interface ListColumn {
  key: string
  name: string
  kind: 'text' | 'check' | 'date' | 'person'
}

/** 리스트의 머리. */
export interface ListHead {
  id: number
  channel: ChannelId
  title: string
  columns: ListColumn[]
  by: UserId
}

/** 리스트의 행. 고칠 때 `version` 을 함께 보낸다. */
export interface ListRow {
  id: number
  cells: Record<string, string | boolean | number>
  version: number
  by: UserId
}

/** 서버에 둔 초안 한 칸. 내 것만 온다. `thread` 가 없으면 본류 입력칸이다. */
export interface ServerDraft {
  channel: ChannelId
  thread: Seq | null
  body: string
  /** RFC 3339. 늦은 쪽이 이긴다. */
  updated_at: string
}

/** 초안을 적을 때 함께 보내는 것. `origin` 은 이 기기의 꼬리표다 — 사건이 돌아오면 제 것을 버린다. */
export interface DraftWrite {
  thread?: Seq | null
  body: string
  updated_at?: string
  origin?: string
}

/** 채널 머리에 걸린 바깥 주소 하나. */
export interface ChannelBookmark {
  id: number
  channel: ChannelId
  title: string
  url: string
  by: UserId
}

/** 폰 하나. APNs 는 16진수, FCM 은 공급자가 준 그대로. */
export interface PushDevice {
  kind: 'apns' | 'fcm' | 'web'
  token: string
}

/** 조직이 쓰는 `:이름:` 하나. 그림은 우리 것이 아니라 주소다. */
export interface CustomEmoji {
  id: number
  name: string
  url: string
}

/** 만들 때도 고칠 때도 **통째로** 준다 — 명단이 반쯤 바뀐 상태를 만들지 않는다. */
export interface GroupEdit {
  handle: string
  name: string
  note: string
  members: UserId[]
}

export interface Command {
  id: string
  name: string
  description: string
  revoked: boolean
  /// 서버가 직접 처리하는 것. 지우거나 고칠 수 없다.
  built_in: boolean
}

export interface App {
  id: string
  name: string
  redirect_uri: string
  scopes: string
  /** 단추를 누르면 알릴 곳. 서명 비밀은 싣지 않는다. */
  interact_url?: string | null
  /** 사건 구독이 연달아 실패해 멈췄다. 관리자가 다시 켜야 한다. */
  events_paused?: boolean
  revoked: boolean
}

export interface Install {
  id: string
  app: string
  app_name: string
  bot: UserId
  scopes: string
  revoked: boolean
}

/** 앱 디렉터리의 분류. 러스트 `domain::directory::Category` 와 같은 이름이다. */
export type AppCategory =
  | 'communication'
  | 'devops'
  | 'monitoring'
  | 'productivity'
  | 'news'
  | 'other'

/** 앱 디렉터리의 한 줄(0077). 살아 있는 앱만 온다. */
export interface DirectoryEntry {
  id: string
  name: string
  category: AppCategory
  summary: string
  /** 앱이 요청하는 권한 범위(스코프 이름). */
  scopes: string[]
  /** 설치(동의) 화면에 넘길 주소. */
  redirect_uri: string
  /** 설치 허용 목록에 있다 — 구성원이 스스로 설치할 수 있다. */
  allowed: boolean
  /** 살아 있는 설치. 제거는 `uninstall(install)`. */
  install: string | null
  requests: number
  /** 내가 설치를 요청했다. */
  requested: boolean
}

export interface InstallRequest {
  app: string
  app_name: string
  user: UserId
  note: string
}

/** 기본 제공 통합 하나. 전부 수신 웹훅 위의 템플릿이다. */
export interface Integration {
  key: string
  bot_name: string
  category: AppCategory
  path: string | null
}

/** 러스트 `Role` 그대로(snake_case). 순서가 곧 높낮이다. */
export type Role = 'external_guest' | 'guest_role' | 'member' | 'admin' | 'owner'

/** 조직의 한 사람(또는 봇). 이름·멘션·초록불의 재료다. */
export interface Member {
  id: UserId
  email: string
  display_name: string
  role: Role
  disabled: boolean
  is_bot: boolean
}

export interface ReadState {
  channel: ChannelId
  read_upto: Seq
  mention_count: number
}

/** 타임라인 한 줄. */
export interface Message {
  id: string
  seq: Seq
  author: UserId
  parent: Seq | null
  /** 스레드 답글인데 채널에도 보냈다. 옛 서버는 안 준다. */
  broadcast?: boolean
  body: Block[]
  client_msg_id: string
  written_at: string
  edited_at: string | null
  deleted_at: string | null
}

/** 한 이모지의 집계. `mine` 은 내가 눌렀는가. */
export interface Tally {
  emoji: string
  count: number
  mine: boolean
  /** 누른 사람들, 누른 순서로 앞의 20명까지. 옛 서버는 안 준다. */
  who?: UserId[]
}

/** 투표 선택지 하나의 집계. `who` 는 고른 사람 앞의 20명 — 익명이면 비어 있다. */
export interface PollChoice {
  text: string
  count: number
  mine: boolean
  who: UserId[]
}

/** 글에 매달린 투표. `closed` 면 결과가 굳었다(만든 사람이 닫았거나 마감이 지났다). */
export interface Poll {
  channel: ChannelId
  seq: Seq
  by: UserId
  question: string
  multi: boolean
  anonymous: boolean
  /** RFC 3339. 없으면 만든 사람이 닫을 때까지 열려 있다. */
  closes_at: string | null
  closed: boolean
  /** 한 표라도 던진 사람 수. */
  voters: number
  options: PollChoice[]
}

export interface PollAsk {
  question: string
  options: string[]
  multi: boolean
  anonymous: boolean
  closes_at: string | null
}

export interface SeqTally {
  seq: Seq
  reactions: Tally[]
}

/** 첨부의 메타. 바이트는 스토리지에 있다(ADR 0006). */
export interface Attachment {
  id: string
  channel: ChannelId
  uploader: UserId
  filename: string
  mime: string
  bytes: number
  created_at: string
}

type Preview = 'image' | 'video' | 'audio' | 'pdf' | 'none'

/** 첨부 하나를 받는 두 길. 주소는 60초짜리 서명이다 — 오래 들고 있지 마라. */
export interface Handout {
  attachment: Attachment
  preview: Preview
  download_slot: string
  preview_slot?: string
}

export interface LinkCard {
  url: string
  title: string | null
  summary: string | null
  image: string | null
  site: string | null
}

export interface Sent {
  seq: Seq
  channel_last: Seq
  /** 같은 `client_msg_id` 가 이미 있었다. 새로 붙은 줄이 아니다. */
  was_resend: boolean
}

/** 서버 오류 몸통(`{ code, message }`). 없거나 모양이 다르면 `null` 이다. */
export interface ErrorBody {
  code: string
  message: string
}

/**
 * 상태 코드만 있을 때의 말. 서버가 사람용 문장을 안 줬을 때 쓴다.
 * 클라이언트는 화면 언어를 모른다 — 화면은 이 문장이 아니라 `status`·`code`·`said` 로
 * 번역한다(`@maru/ui` 의 `why`). 이 말은 로그의 몫이라 짧게 둔다(첫 화면 번들에 실린다).
 */
function plain_words(status: number): string {
  if (status === 401) return '로그인 만료'
  if (status === 404) return '찾을 수 없음'
  if (status === 429) return '요청이 너무 많음'
  if (status >= 500) return '서버 응답 없음'
  return `실패 (${status})`
}

/** 캔버스의 머리. 러스트 `routes::canvas::MetaOut`. */
export interface CanvasMeta {
  channel: ChannelId
  title: string
  by: UserId
  made_at: string
  /** 마지막 조각 번호. */
  upto: number
}

/** 러스트 `routes::canvas::StateOut`. 조각은 base64 다. */
export interface CanvasState extends CanvasMeta {
  base: number
  snapshot: string | null
  updates: { seq: number; data: string }[]
}

export class ApiError extends Error {
  status: number
  req_id: string | null
  /** 429 일 때 서버가 알려 준 대기 초. */
  retry_secs: number | null
  /** 서버가 준 기계용 코드(`TooBroad`·`Conflict`…). 분기는 이것으로 한다. */
  code: string | null
  /**
   * 서버가 몸통에 적은 말 그대로. 409 면 기계용 이유(`ai_off`)이고, 400·422 면 한국어
   * 문장이다. 없거나 `none` 이면 `null` — 그때 `message` 는 상태로 만든 문장이다.
   */
  said: string | null

  /**
   * `message` 는 **사람이 읽는 문장**이다. 패널들이 그대로 화면에 띄운다.
   * 서버가 준 문장이 있으면 그것을, 없거나 `none`(404 를 뭉개는 자리)이면
   * 상태 코드로 만든 문장을 쓴다. 요청 번호는 문장에 섞지 않는다 — 사람에게
   * 필요한 건 무엇을 할지이고, 번호는 `req_id` 로 따로 있다.
   */
  constructor(
    status: number,
    req_id: string | null,
    retry_secs: number | null,
    body: ErrorBody | null = null,
  ) {
    const got = body?.message
    const said = got !== undefined && got !== '' && got !== 'none' ? got : null
    super(said ?? plain_words(status))
    this.name = 'ApiError'
    this.status = status
    this.req_id = req_id
    this.retry_secs = retry_secs
    this.code = body?.code ?? null
    this.said = said
  }
}

/** 실패 응답의 몸통을 읽는다. JSON 이 아니거나 모양이 다르면 `null` — 던지지 않는다. */
export async function error_body(res: Response): Promise<ErrorBody | null> {
  try {
    const got: unknown = await res.json()
    if (
      typeof got === 'object' &&
      got !== null &&
      typeof (got as ErrorBody).code === 'string' &&
      typeof (got as ErrorBody).message === 'string'
    ) {
      return got as ErrorBody
    }
  } catch {
    // 프록시가 HTML 을 돌려준 경우 등. 상태 코드만으로 말한다.
  }
  return null
}

export interface ApiConfig {
  /** `''` 이면 같은 오리진(개발 프록시·운영 리버스 프록시). 끝에 `/` 를 두지 않는다. */
  base_url: string
  /** 접근 토큰. 부를 때마다 최신 것을 돌려줘야 한다. */
  token(): Promise<string>
  /** 시험에서 바꿔 끼운다. 기본은 전역 `fetch`. */
  fetch?: typeof fetch
}

export class MaruApi {
  #config: ApiConfig

  constructor(config: ApiConfig) {
    this.#config = config
  }

  /** 한 번 쓰는 소켓 입장권. **받자마자 붙어야 한다** — 30초 뒤엔 없는 티켓이다. */
  async ticket(): Promise<string> {
    const mail_body = await this.#call<{ ticket: string; ttl_secs: number }>(
      'POST',
      '/v1/socket-ticket',
    )
    return mail_body.ticket
  }

  my_channels(): Promise<Channel[]> {
    return this.#call('GET', '/v1/channels')
  }

  channel(id: ChannelId): Promise<Channel> {
    return this.#call('GET', `/v1/channels/${id}`)
  }

  /**
   * 들어갈 수 있는 공개 채널. 이미 든 곳과 보관한 곳은 안 온다. 서버는 이름순으로
   * 한 쪽(최대 `CHANNEL_PAGE_MAX`)씩 주므로 끝까지 이어 받아 한 목록으로 돌려준다.
   */
  browse_channels(): Promise<Channel[]> {
    return this.#all_pages('/v1/channels/public', CHANNEL_PAGE_MAX)
  }

  /** 보관한 채널 중 내가 볼 수 있는 것. 되살리기는 `update_channel(id, {stored:false})`. 쪽은 찾아보기와 같다. */
  archived_channels(): Promise<Channel[]> {
    return this.#all_pages('/v1/channels/archived', CHANNEL_PAGE_MAX)
  }

  /** 새로 든 사람이 저절로 드는 공개 채널들. */
  default_channels(): Promise<ChannelId[]> {
    return this.#call('GET', '/v1/org/default-channels')
  }

  /** 기본 채널로 정하거나 푼다(관리자). */
  async set_default_channel(id: ChannelId, on: boolean): Promise<void> {
    await this.#call(on ? 'PUT' : 'DELETE', `/v1/org/default-channels/${id}`)
  }

  /**
   * 이름·주제·보관을 고친다. **빠진 열쇠는 "그대로"** 이고,
   * `topic: null` 은 "비워라" 다. 빈 패치는 서버가 거절한다.
   */
  update_channel(id: ChannelId, patch: ChannelPatch): Promise<Channel> {
    return this.#call('PATCH', `/v1/channels/${id}`, patch)
  }

  /**
   * 채널을 영구히 지운다(204). 고치기와 같은 사람만 — 대화방·기본 채널은 409.
   * 옛 구성원에게는 각자 "나갔다" 사건이 간다.
   */
  remove_channel(id: ChannelId): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${id}`)
  }

  /** 그룹 대화를 이 이름의 비공개 채널로 바꾼다. 안에 있는 사람이면 누구나 된다. 되돌리기는 없다. */
  convert_dm(id: ChannelId, name: string): Promise<Channel> {
    return this.#call('POST', `/v1/channels/${id}/convert`, { name })
  }

  /** 채널 관리자 명단. 구성원이면 누구나 본다. */
  channel_managers(channel: ChannelId): Promise<UserId[]> {
    return this.#call('GET', `/v1/channels/${channel}/managers`)
  }

  /** 채널 관리자로 정하거나 푼다. 조직 관리자나 그 채널의 관리자만, 대상은 구성원이어야 한다. */
  set_channel_manager(channel: ChannelId, who: UserId, on: boolean): Promise<void> {
    return this.#call(on ? 'PUT' : 'DELETE', `/v1/channels/${channel}/managers/${who}`)
  }

  /** 내가 낀 스레드들. 마지막 답글이 최근인 순이다. */
  my_threads(): Promise<ThreadBrief[]> {
    return this.#call('GET', '/v1/threads')
  }

  /** 채널의 핀. 지워진 줄은 빠진다. */
  pins(channel: ChannelId): Promise<Marked[]> {
    return this.#call('GET', `/v1/channels/${channel}/pins`)
  }

  lists(channel: ChannelId): Promise<ListHead[]> {
    return this.#call('GET', `/v1/channels/${channel}/lists`)
  }

  list_one(channel: ChannelId, id: number): Promise<ListHead & { rows: ListRow[] }> {
    return this.#call('GET', `/v1/channels/${channel}/lists/${id}`)
  }

  list_create(
    channel: ChannelId,
    title: string,
    columns: { name: string; kind: ListColumn['kind'] }[],
  ): Promise<ListHead> {
    return this.#call('POST', `/v1/channels/${channel}/lists`, { title, columns })
  }

  /** 이름·열 고치기. 있던 열은 `key` 를 싣는다(종류는 못 바꾼다). 뺀 열의 칸은 지워진다. 만든 사람과 관리자만. */
  list_edit(
    channel: ChannelId,
    id: number,
    title: string,
    columns: { key?: string; name: string; kind: ListColumn['kind'] }[],
  ): Promise<ListHead> {
    return this.#call('PATCH', `/v1/channels/${channel}/lists/${id}`, { title, columns })
  }

  /** 만든 사람과 관리자만 된다. 남에게는 404 다. */
  list_remove(channel: ChannelId, id: number): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/lists/${id}`)
  }

  list_add_row(channel: ChannelId, id: number, cells: ListRow['cells']): Promise<ListRow> {
    return this.#call('POST', `/v1/channels/${channel}/lists/${id}/rows`, { cells })
  }

  /** 본 버전이 낡았으면 409(`ApiError.status`). 빈 글은 칸을 비운다. */
  list_edit_row(
    channel: ChannelId,
    id: number,
    row: number,
    version: number,
    cells: ListRow['cells'],
  ): Promise<ListRow> {
    const route = `/v1/channels/${channel}/lists/${id}/rows/${row}`
    return this.#call('PATCH', route, { version, cells })
  }

  list_remove_row(channel: ChannelId, id: number, row: number): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/lists/${id}/rows/${row}`)
  }

  /** 내 초안 전부(서버 저장분). 지금 볼 수 있는 채널의 것만. */
  drafts(): Promise<ServerDraft[]> {
    return this.#call('GET', '/v1/drafts')
  }

  /** 초안을 적는다. 돌아오는 것은 **이긴** 초안이다. 빈 글이면 지워지고 `undefined`. */
  draft_put(channel: ChannelId, write: DraftWrite): Promise<ServerDraft | undefined> {
    return this.#call('PUT', `/v1/drafts/${channel}`, write)
  }

  /** 초안을 지운다(보냈거나 비웠다). 없던 것도 성공이다. */
  draft_drop(channel: ChannelId, thread?: Seq | null, origin?: string): Promise<void> {
    const q = new URLSearchParams()
    if (thread !== undefined && thread !== null) q.set('thread', String(thread))
    if (origin !== undefined) q.set('origin', origin)
    const tail = q.size === 0 ? '' : `?${q}`
    return this.#call('DELETE', `/v1/drafts/${channel}${tail}`)
  }

  /** 채널의 북마크. 건 순서다. */
  bookmarks(channel: ChannelId): Promise<ChannelBookmark[]> {
    return this.#call('GET', `/v1/channels/${channel}/bookmarks`)
  }

  /** 북마크를 건다. 제목이 비면 주소가 제목이다. */
  bookmark_add(channel: ChannelId, title: string, url: string): Promise<ChannelBookmark> {
    return this.#call('POST', `/v1/channels/${channel}/bookmarks`, { title, url })
  }

  /** 북마크의 제목·주소를 고친다. 제목이 비면 주소가 제목이다. */
  bookmark_edit(
    channel: ChannelId,
    id: number,
    title: string,
    url: string,
  ): Promise<ChannelBookmark> {
    return this.#call('PATCH', `/v1/channels/${channel}/bookmarks/${id}`, { title, url })
  }

  bookmark_remove(channel: ChannelId, id: number): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/bookmarks/${id}`)
  }

  /**
   * 채널 캔버스. `since` 는 가진 마지막 조각 번호다. 그보다 앞이 접혔으면 `snapshot` 이
   * 함께 온다 — 가진 문서에 그대로 적용한다(CRDT 라 두 번 적용해도 같다). 없으면 404.
   */
  canvas(channel: ChannelId, since?: number): Promise<CanvasState> {
    const q = since === undefined ? '' : `?since=${since}`
    return this.#call('GET', `/v1/channels/${channel}/canvas${q}`)
  }

  /** 채널 캔버스를 만든다. 이미 있으면 409(`canvas_exists`). */
  canvas_create(channel: ChannelId, title: string): Promise<CanvasMeta> {
    return this.#call('POST', `/v1/channels/${channel}/canvas`, { title })
  }

  canvas_retitle(channel: ChannelId, title: string): Promise<CanvasMeta> {
    return this.#call('PATCH', `/v1/channels/${channel}/canvas`, { title })
  }

  /** 지운다. 만든 사람·채널 관리자·조직 관리자만 — 아니면 404. */
  canvas_remove(channel: ChannelId): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/canvas`)
  }

  /** Yjs 조각(v1) 하나를 쌓는다. 붙은 번호를 돌려준다. */
  canvas_push(channel: ChannelId, data: string): Promise<{ seq: number }> {
    return this.#call('POST', `/v1/channels/${channel}/canvas/updates`, { data })
  }

  /** 대화방을 사이드바에서 닫거나(on) 다시 편다. 나가기가 아니다. */
  close_dm(channel: ChannelId, on: boolean): Promise<void> {
    return this.#call(on ? 'PUT' : 'DELETE', `/v1/channels/${channel}/close`)
  }

  /** 핀을 꽂거나 뺀다. 멱등이다 — 연타해도 같은 자리다. */
  pin(channel: ChannelId, seq: Seq, on: boolean): Promise<void> {
    const route = `/v1/channels/${channel}/messages/${seq}/pin`
    return this.#call(on ? 'PUT' : 'DELETE', route)
  }

  /** 이 스레드를 따르는가. 참여했거나 손으로 팔로우했으면 `true`. */
  async thread_following(channel: ChannelId, root: Seq): Promise<boolean> {
    const got: { following: boolean } = await this.#call(
      'GET',
      `/v1/channels/${channel}/messages/${root}/follow`,
    )
    return got.following
  }

  /** 스레드를 팔로우하거나 알림을 끈다. 멱등이다. */
  follow_thread(channel: ChannelId, root: Seq, on: boolean): Promise<void> {
    const route = `/v1/channels/${channel}/messages/${root}/follow`
    return this.#call(on ? 'PUT' : 'DELETE', route)
  }

  /** 내가 담아 둔 줄들. 채널을 가로질러 최근 담은 순이다. */
  saved_items(): Promise<Marked[]> {
    return this.#call('GET', '/v1/saved')
  }

  save_item(channel: ChannelId, seq: Seq, on: boolean): Promise<void> {
    const route = `/v1/channels/${channel}/messages/${seq}/save`
    return this.#call(on ? 'PUT' : 'DELETE', route)
  }

  /** 담아 둔 줄의 탭을 옮기거나 기한을 건다. 담지 않은 줄이면 404. */
  later_item(channel: ChannelId, seq: Seq, patch: LaterPatch): Promise<void> {
    return this.#call('PATCH', `/v1/channels/${channel}/messages/${seq}/save`, patch)
  }

  /** 내가 정해 둔 채널별 취향. 기본값뿐인 채널은 안 온다. */
  my_prefs(): Promise<ChannelPref[]> {
    return this.#call('GET', '/v1/channels/prefs')
  }

  /** 알림 단계·즐겨찾기·섹션. 빈 패치는 서버가 거절한다. */
  /** 내 사이드바 칸들, 순서대로. 채널 취향에만 이름이 있는 옛 칸은 끝에 붙어 온다. */
  sections(): Promise<SidebarSection[]> {
    return this.#call('GET', '/v1/me/sections')
  }

  /** 칸 목록을 통째로 갈아 끼운다. 빠진 칸의 채널은 기본 칸으로 돌아간다. */
  set_sections(sections: readonly SidebarSection[]): Promise<SidebarSection[]> {
    return this.#call('PUT', '/v1/me/sections', { sections })
  }

  /** 칸 이름을 바꾼다. 이미 있는 이름이면 409. */
  rename_section(from: string, to: string): Promise<SidebarSection[]> {
    return this.#call('POST', '/v1/me/sections/rename', { from, to })
  }

  set_pref(id: ChannelId, patch: PrefPatch): Promise<ChannelPref> {
    return this.#call('PATCH', `/v1/channels/${id}/pref`, patch)
  }

  /** 공개·비공개 채널만. 대화(DM)는 `open_dm` — 이름이 없고 사람이 곧 정체성이다. */
  create_channel(kind: 'public' | 'private', name: string): Promise<Channel> {
    return this.#call('POST', '/v1/channels', { kind, name })
  }

  /** 멱등이다. 같은 사람들과의 대화가 있으면 그것을 돌려준다(200), 없으면 만든다(201). */
  open_dm(with_: readonly UserId[]): Promise<Channel> {
    return this.#call('POST', '/v1/dms', { with: with_ })
  }

  join(channel: ChannelId): Promise<void> {
    return this.#call('POST', `/v1/channels/${channel}/join`)
  }

  leave(channel: ChannelId): Promise<void> {
    return this.#call('POST', `/v1/channels/${channel}/leave`)
  }

  /** 남을 들인다. 이미 있어도 성공이다. */
  invite(channel: ChannelId, who: UserId): Promise<void> {
    return this.#call('POST', `/v1/channels/${channel}/members/${who}`)
  }

  /** 여럿을 한 요청으로. 답은 새로 든 사람만이다. 못 들일 사람이 섞이면 아무도 안 든다(404). */
  invite_many(channel: ChannelId, who: readonly UserId[]): Promise<{ added: UserId[] }> {
    return this.#call('POST', `/v1/channels/${channel}/members`, { who })
  }

  kick(channel: ChannelId, who: UserId): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/members/${who}`)
  }

  /** 검색어는 사람이 쓴 글이라 질의 문자열이 아니라 몸통에 싣는다(로그에 안 남게). */
  search(ask: SearchAsk): Promise<Hit[]> {
    return this.#call('POST', '/v1/search', ask)
  }

  /** 파일 이름으로. 커서는 앞 쪽 마지막의 `attachment.created_at`. */
  search_files(ask: FileAsk): Promise<FileHit[]> {
    return this.#call('POST', '/v1/search/files', ask)
  }

  // ── AI 요약·검색 답 ──
  // 꺼져 있으면 409 `ai_off`, 모델이 거절하면 `ai_refused`, 붐비면 `ai_busy`(메시지에 실린다).

  /** 내 조직에서 쓸 수 있는가(서버 ∧ 조직). 꺼져 있으면 화면은 단추를 그리지 않는다. */
  ai_status(): Promise<{ enabled: boolean }> {
    return this.#call('GET', '/v1/ai')
  }

  /** 조직의 AI 스위치와 서버가 켜져 있는지. 관리자만 — 아니면 404. */
  ai_org(): Promise<{ enabled: boolean; server: boolean }> {
    return this.#call('GET', '/v1/org/ai')
  }

  ai_org_set(enabled: boolean): Promise<void> {
    return this.#call('PUT', '/v1/org/ai', { enabled })
  }

  /** 채널·스레드 요약. 요약할 줄이 없으면 `null`. */
  summarize(channel: ChannelId, of: SummaryOf): Promise<AiDigest | null> {
    return this.#call('POST', `/v1/channels/${channel}/summary`, of)
  }

  /** 검색 결과로 짓는 답. 질문은 사람이 쓴 글이라 몸통으로 간다. 근거가 없으면 `null`. */
  search_answer(question: string): Promise<AiDigest | null> {
    return this.#call('POST', '/v1/search/answer', { question })
  }

  // ── 허들 ──
  // 꺼져 있으면 409 `huddle_off`, 가득 차면 409 `huddle_full`, 안 든 채널은 404.

  /** 켜져 있는가, 그리고 내가 든 채널들에 열린 허들. */
  huddles(): Promise<{ enabled: boolean; live: HuddleLive[] }> {
    return this.#call('GET', '/v1/huddles')
  }

  /** 들어간다. 이미 있으면 맥박이다 — 서버는 맥박이 끊기면 뺀다. */
  huddle_join(channel: ChannelId): Promise<{ ticket: HuddleTicket; people: UserId[] }> {
    return this.#call('POST', `/v1/channels/${channel}/huddle`)
  }

  /** 나온다. 없던 자리여도 성공이다. */
  huddle_leave(channel: ChannelId): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/huddle`)
  }

  // ── 클립 받아쓰기 ──
  // 서버에 음성 인식 공급자가 설정됐을 때만 켜진다. 꺼져 있으면 `transcribe` 는 409 `stt_off`.
  // 소리는 우리 서버로만 간다 — 브라우저 음성 인식(바깥 서버로 보낸다)은 쓰지 않는다.

  stt_status(): Promise<{ enabled: boolean }> {
    return this.#call('GET', '/v1/stt')
  }

  async transcribe(audio: Blob): Promise<{ text: string }> {
    const token = await this.#config.token()
    const send = this.#config.fetch ?? fetch
    const res = await send(`${this.#config.base_url}/v1/stt`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        // 코덱 꼬리(`;codecs=opus`)는 떼고 보낸다. 서버는 `audio/*` 만 본다.
        'content-type': audio.type.split(';')[0] || 'application/octet-stream',
      },
      body: audio,
      credentials: 'omit',
    })
    if (!res.ok) {
      const again = res.headers.get('retry-after')
      throw new ApiError(
        res.status,
        res.headers.get('x-request-id'),
        again === null ? null : Number.parseInt(again, 10) || null,
        await error_body(res),
      )
    }
    return (await res.json()) as { text: string }
  }

  // ── 구성원·초대(관리자) ──

  change_role(who: UserId, role: Role): Promise<void> {
    return this.#call('PATCH', `/v1/members/${who}/role`, { role })
  }

  set_disabled(who: UserId, disable: boolean): Promise<void> {
    return this.#call('PATCH', `/v1/members/${who}/active`, { disable })
  }

  /** 내 이름만. 남의 이름은 경로가 없다. */
  rename_me(display_name: string): Promise<void> {
    return this.#call('PATCH', '/v1/members/me/name', { display_name })
  }

  /** 조직 전체의 프로필. 사람 목록과 짝지어 쓴다 — 사람 수만큼 묻지 않는다. */
  profiles(): Promise<ProfileRow[]> {
    return this.#call('GET', '/v1/profiles')
  }

  /** 한 사람의 프로필. */
  profile(who: UserId): Promise<ProfileRow> {
    return this.#call('GET', `/v1/profiles/${who}`)
  }

  /** 내 프로필만. 남의 상태를 대신 적는 길은 없다. */
  set_profile(patch: ProfilePatch): Promise<ProfileRow> {
    return this.#call('PATCH', '/v1/profiles/me', patch)
  }

  /** 내 얼굴 사진 올릴곳(60초). 종류·크기가 서명에 들어 있다. */
  face_begin(meta: { mime: string; bytes: number }): Promise<{ id: string; upload_slot: string }> {
    return this.#call('POST', '/v1/profiles/me/face', meta)
  }

  /** 올린 사진을 내 얼굴로 건다. 앞 사진은 서버가 치운다. */
  face_set(id: string): Promise<{ user: UserId; url: string }> {
    return this.#call('PUT', '/v1/profiles/me/face', { id })
  }

  face_drop(): Promise<void> {
    return this.#call('DELETE', '/v1/profiles/me/face')
  }

  /** 물어본 사람 중 사진이 있는 사람만 온다. 주소는 30분 눈금으로 같아 브라우저 캐시가 먹는다. */
  faces(who: readonly UserId[]): Promise<{ user: UserId; url: string }[]> {
    if (who.length === 0) return Promise.resolve([])
    return this.#call('POST', '/v1/faces', { who })
  }

  invites(): Promise<Invite[]> {
    return this.#call('GET', '/v1/invites')
  }

  invite_send(email: string, role: Role): Promise<void> {
    return this.#call('POST', '/v1/invites', { email, role })
  }

  invite_revoke(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/invites/${id}`)
  }

  /** 공유 초대 링크(G30) — 관리자만. 주소에 묶이지 않고, 들어온 사람은 늘 구성원이다. */
  invite_links(): Promise<InviteLink[]> {
    return this.#call('GET', '/v1/invite-links')
  }

  /** 수명은 1·7·30일 중 하나(기본 7). 링크 주소는 이 응답에만 실린다. */
  invite_link_make(days: number): Promise<InviteLinkMade> {
    return this.#call('POST', '/v1/invite-links', { days })
  }

  invite_link_revoke(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/invite-links/${id}`)
  }

  // ── 내 기기·비밀번호 ──

  devices(): Promise<Device[]> {
    return this.#call('GET', '/v1/auth/devices')
  }

  device_revoke(lineage: string): Promise<void> {
    return this.#call('DELETE', `/v1/auth/devices/${lineage}`)
  }

  /** 주소는 보내지 않는다 — 토큰이 가리키는 자리의 주소만 바뀐다. `current` 는 처음 정할 때 없다. */
  password_change(current: string | null, fresh: string): Promise<void> {
    return this.#call('POST', '/v1/auth/password', { current, fresh })
  }

  // ── 2단계 인증(내 것)·조직 보안 정책(관리자) ──

  two_factor(): Promise<TwoFactorStatus> {
    return this.#call('GET', '/v1/me/2fa')
  }

  /** 새 키를 받는다(아직 꺼진 채). 앱 코드를 한 번 맞혀야 켜진다. */
  two_factor_setup(): Promise<TotpSetup> {
    return this.#call('POST', '/v1/me/2fa/setup', {})
  }

  /** 켠다. 백업 코드는 **이 응답에서 한 번만** 온다. */
  two_factor_enable(code: string): Promise<{ backup_codes: string[] }> {
    return this.#call('POST', '/v1/me/2fa/enable', { code })
  }

  /** 백업 코드를 새 벌로. 옛것은 전부 죽는다. 지금 코드가 필요하다. */
  two_factor_backups(code: string): Promise<{ backup_codes: string[] }> {
    return this.#call('POST', '/v1/me/2fa/backup-codes', { code })
  }

  /** 끈다. 지금 코드가 필요하고, 요구하는 조직이 있으면 409. */
  two_factor_disable(code: string): Promise<void> {
    return this.#call('POST', '/v1/me/2fa/disable', { code })
  }

  /** 관리자가 구성원의 2단계 인증을 푼다(폰도 백업 코드도 잃은 사람). */
  two_factor_reset(who: UserId): Promise<void> {
    return this.#call('DELETE', `/v1/members/${who}/2fa`)
  }

  security(): Promise<SecurityPolicy> {
    return this.#call('GET', '/v1/org/security')
  }

  security_set(policy: SecurityPolicy): Promise<SecurityPolicy> {
    return this.#call('PUT', '/v1/org/security', policy)
  }

  /** 이 API 의 바깥 주소. 같은 오리진이면 지금 페이지의 오리진이다(SCIM 주소를 보여 줄 때 쓴다). */
  get origin(): string {
    return this.#config.base_url || globalThis.location?.origin || ''
  }

  // ── SSO(소유자)·SCIM 토큰(관리자). 비밀은 적을 때만 보내고 다시 받지 않는다 ──

  sso(): Promise<SsoShown> {
    return this.#call('GET', '/v1/org/sso')
  }

  /** `client_secret` 을 비우면 있던 비밀을 그대로 둔다. 발급자는 서버가 IdP 에 물어 본다(409). */
  sso_set(input: SsoInput): Promise<SsoConfig> {
    return this.#call('PUT', '/v1/org/sso', input)
  }

  sso_remove(): Promise<void> {
    return this.#call('DELETE', '/v1/org/sso')
  }

  scim_tokens(): Promise<ScimToken[]> {
    return this.#call('GET', '/v1/org/scim-tokens')
  }

  /** 원본 토큰은 이 답에만 있다. */
  scim_token_create(label: string): Promise<{ id: number; token: string }> {
    return this.#call('POST', '/v1/org/scim-tokens', { label })
  }

  scim_token_revoke(id: number): Promise<void> {
    return this.#call('DELETE', `/v1/org/scim-tokens/${id}`)
  }

  /** 내가 든 채널 중 바깥 조직과 이어진 것(Slack Connect). */
  connect_mine(): Promise<ConnectLink[]> {
    return this.#call('GET', '/v1/connect')
  }

  /** 한 채널의 이음. 채널을 볼 수 있으면 누구나. */
  connect_of(channel: ChannelId): Promise<ConnectLink[]> {
    return this.#call('GET', `/v1/channels/${channel}/connect`)
  }

  /** 채널을 바깥에 잇자는 초대(관리자). 토큰은 이 답에만 있다. */
  connect_invite(channel: ChannelId): Promise<ConnectInvite> {
    return this.#call('POST', `/v1/channels/${channel}/connect`)
  }

  /** 끊는다(관리자). 손님 쪽 채널은 보관된다. */
  connect_unlink(channel: ChannelId, peer: number): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/connect/${peer}`)
  }

  /** 받기 전에 들여다본다(관리자). 토큰은 몸통에 싣는다 — 주소에 실으면 로그에 남는다. */
  connect_peek(token: string): Promise<ConnectPending> {
    return this.#call('POST', '/v1/connect/peek', { token })
  }

  /** 받는다(관리자). 내 조직에 새 채널이 열린다. */
  connect_accept(token: string, name: string, is_private: boolean): Promise<Channel> {
    return this.#call('POST', '/v1/connect/accept', { token, name, private: is_private })
  }

  /** 조직 연결 정책(채널당 상한·외부 DM 허용). 읽기는 누구나. */
  connect_policy(): Promise<ConnectPolicy> {
    return this.#call('GET', '/v1/connect/policy')
  }

  /** 정책을 바꾼다(관리자). */
  connect_policy_set(policy: ConnectPolicy): Promise<ConnectPolicy> {
    return this.#call('PUT', '/v1/connect/policy', policy)
  }

  /** DM 을 요청할 수 있는 외부 사용자(이 워크스페이스에 앉은 바깥 사람). */
  connect_people(): Promise<ConnectPerson[]> {
    return this.#call('GET', '/v1/connect/people')
  }

  /** 외부 사용자에게 DM 을 요청한다. 상대가 수락해야 열린다. */
  connect_dm_ask(user: UserId | number): Promise<{ id: string }> {
    return this.#call('POST', '/v1/connect/dm', { user })
  }

  /** 나에게 온 DM 요청. */
  connect_dm_invites(): Promise<ConnectDmInvite[]> {
    return this.#call('GET', '/v1/connect/dm')
  }

  /** 수락 — 양쪽에 대화방이 열린다. 내 쪽 채널을 돌려준다. */
  connect_dm_accept(id: string): Promise<Channel> {
    return this.#call('POST', `/v1/connect/dm/${encodeURIComponent(id)}/accept`)
  }

  connect_dm_decline(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/connect/dm/${encodeURIComponent(id)}`)
  }

  // ── 연동(관리자). 비밀은 만들 때 한 번만 온다 ──

  webhooks(): Promise<Webhook[]> {
    return this.#call('GET', '/v1/webhooks')
  }

  webhook_create(
    channel: ChannelId,
    name: string,
  ): Promise<{ id: string; bot: UserId; token: string }> {
    return this.#call('POST', '/v1/webhooks', { channel, name })
  }

  webhook_revoke(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/webhooks/${id}`)
  }

  outgoing_hooks(): Promise<OutgoingHook[]> {
    return this.#call('GET', '/v1/outgoing-hooks')
  }

  outgoing_create(
    channel: ChannelId,
    name: string,
    url: string,
  ): Promise<{ id: string; secret: string }> {
    return this.#call('POST', '/v1/outgoing-hooks', { channel, name, url })
  }

  outgoing_revoke(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/outgoing-hooks/${id}`)
  }

  workflows(): Promise<Workflow[]> {
    return this.#call('GET', '/v1/workflows')
  }

  workflow_create(draft: WorkflowDraft): Promise<WorkflowCreated> {
    return this.#call('POST', '/v1/workflows', draft)
  }

  workflow_set(id: number, enabled: boolean): Promise<void> {
    return this.#call('PATCH', `/v1/workflows/${id}`, { enabled })
  }

  workflow_delete(id: number): Promise<void> {
    return this.#call('DELETE', `/v1/workflows/${id}`)
  }

  /**
   * 슬래시 커맨드를 부른다. 답은 **부른 사람에게만** 보이고 채널에 남지 않는다 —
   * 그래서 여기서 돌아온 글은 타임라인이 아니라 귀띔으로 그린다.
   */
  command_run(
    channel: ChannelId,
    name: string,
    text: string,
  ): Promise<{ text: string; remind_at?: string; snooze?: { until: string | null } }> {
    return this.#call('POST', '/v1/commands/run', { channel, name, text })
  }

  /**
   * 사용자 그룹(`@팀이름`). 목록은 누구나 읽는다 — 작성창의 `@` 자동완성이 쓴다.
   * 만들고 고치고 지우는 일은 관리자만 되고, 아니면 404 다.
   */
  groups(): Promise<UserGroup[]> {
    return this.#call('GET', '/v1/groups')
  }

  group_create(body: GroupEdit): Promise<UserGroup> {
    return this.#call('POST', '/v1/groups', body)
  }

  group_edit(id: GroupId, body: GroupEdit): Promise<UserGroup> {
    return this.#call('PATCH', `/v1/groups/${id}`, body)
  }

  group_remove(id: GroupId): Promise<void> {
    return this.#call('DELETE', `/v1/groups/${id}`)
  }

  highlights(): Promise<{ words: string[] }> {
    return this.#call('GET', '/v1/me/highlights')
  }

  highlights_set(words: string[]): Promise<{ words: string[] }> {
    return this.#call('PUT', '/v1/me/highlights', { words })
  }

  /** 내 보기 설정(테마·강조색·언어). 이 조직의 모든 기기에 따라온다. 다크 모드는 기기의 것이라 여기 없다. */
  appearance(): Promise<Appearance> {
    return this.#call('GET', '/v1/me/appearance')
  }

  /** 통째로 바꾼다. 강조색은 `#rrggbb` 만 — 아니면 400. */
  appearance_set(look: Appearance): Promise<Appearance> {
    return this.#call('PUT', '/v1/me/appearance', look)
  }

  /** 메일 요약 주기(분). `null` 이면 꺼짐. 메일에는 개수만 실린다. */
  digest(): Promise<{ every_min: number | null }> {
    return this.#call('GET', '/v1/me/digest')
  }

  digest_set(every_min: 15 | 60 | 240 | 1440 | null): Promise<void> {
    return this.#call('PUT', '/v1/me/digest', { every_min })
  }

  /** 조직의 보존 기간(일). `null` 이면 영원. 관리자만 — 아니면 404. */
  retention(): Promise<{ days: number | null }> {
    return this.#call('GET', '/v1/org/retention')
  }

  retention_set(days: number | null): Promise<void> {
    return this.#call('PUT', '/v1/org/retention', { days })
  }

  /** 감사 로그, 최신순. 관리자만 — 아니면 404. 커서는 마지막 줄의 `id`. */
  audit(q: AuditQuery = {}): Promise<AuditRow[]> {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    }
    const tail = qs.size > 0 ? `?${qs}` : ''
    return this.#call('GET', `/v1/org/audit${tail}`)
  }

  /** 공개 채널 내보내기를 요청한다. 기다리는 요청이 있으면 그것이 돌아온다. 관리자만. */
  export_request(): Promise<ExportJob> {
    return this.#call('POST', '/v1/org/exports', {})
  }

  exports(): Promise<ExportJob[]> {
    return this.#call('GET', '/v1/org/exports')
  }

  /** 끝났고 7일 안이면 `url` 이 찬다. 수명이 짧아 누를 때마다 다시 받는다. */
  export_one(id: number): Promise<ExportJob & { url: string | null }> {
    return this.#call('GET', `/v1/org/exports/${id}`)
  }

  /** 내 폰 목록. 셸이 공급자에게서 받은 토큰을 건다 — 알림에는 본문이 실리지 않는다. */
  push_devices(): Promise<PushDevice[]> {
    return this.#call('GET', '/v1/me/push-devices')
  }

  /** 브라우저 구독용 VAPID 공개 키. `null` 이면 서버가 웹 푸시를 켜지 않았다. */
  async push_web_key(): Promise<string | null> {
    const r: { key: string | null } = await this.#call('GET', '/v1/push/web-key')
    return r.key
  }

  /** 같은 토큰을 다시 걸어도 한 줄이다. 로그인할 때마다 불러도 된다. */
  push_register(device: PushDevice): Promise<void> {
    return this.#call('PUT', '/v1/me/push-devices', device)
  }

  /** 로그아웃 전에 부른다. 없는 토큰도 성공이다. */
  push_unregister(token: string): Promise<void> {
    return this.#call('DELETE', `/v1/me/push-devices/${encodeURIComponent(token)}`)
  }

  emoji(): Promise<CustomEmoji[]> {
    return this.#call('GET', '/v1/emoji')
  }

  /** 그림은 바깥 `url`(https) 이나 먼저 올린 `picture` 번호 둘 중 하나. */
  emoji_create(
    body: { name: string; url: string } | { name: string; picture: string },
  ): Promise<CustomEmoji> {
    return this.#call('POST', '/v1/emoji', body)
  }

  /** 파일로 달 이모지의 올릴곳(60초, 관리자만). 종류·크기가 서명에 들어 있다. */
  emoji_picture(meta: {
    mime: string
    bytes: number
  }): Promise<{ id: string; upload_slot: string }> {
    return this.#call('POST', '/v1/emoji/picture', meta)
  }

  emoji_remove(id: number): Promise<void> {
    return this.#call('DELETE', `/v1/emoji/${id}`)
  }

  commands(): Promise<Command[]> {
    return this.#call('GET', '/v1/commands')
  }

  command_create(
    name: string,
    description: string,
    url: string,
  ): Promise<{ id: string; name: string; secret: string }> {
    return this.#call('POST', '/v1/commands', { name, description, url })
  }

  command_revoke(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/commands/${id}`)
  }

  apps(): Promise<App[]> {
    return this.#call('GET', '/v1/apps')
  }

  app_register(
    name: string,
    redirect_uri: string,
    scopes: string,
  ): Promise<{ client_id: string; client_secret: string }> {
    return this.#call('POST', '/v1/apps', { name, redirect_uri, scopes })
  }

  /**
   * 이 앱에게 내 이름으로 움직일 권한을 준다. 돌아오는 `redirect_uri` 는
   * **서버가 확인한 것**이다 — 주소창이 준 값이 아니라 이것으로 보내야 한다.
   */
  app_authorize(
    id: string,
    redirect_uri: string,
    scopes: string,
  ): Promise<{ code: string; redirect_uri: string }> {
    return this.#call('POST', `/v1/apps/${id}/authorize`, { redirect_uri, scopes })
  }

  /**
   * 앱의 상호작용 주소를 달거나(`url`) 뗀다(`null`). 달 때마다 새 서명 비밀이
   * **이 응답에만 한 번** 온다.
   */
  app_interactivity(id: string, url: string | null): Promise<{ signing_secret: string | null }> {
    return this.#call('PUT', `/v1/apps/${id}/interactivity`, { url })
  }

  /** 한 앱의 사건 구독(관리자). 없으면 `null`. */
  app_events(id: string): Promise<AppEventSub | null> {
    return this.#call('GET', `/v1/apps/${id}/events`)
  }

  /**
   * 사건 구독을 갈아 끼운다(관리자). 주소가 있으면 서버가 `url_verification` 도전을 먼저
   * 보내고, 맞게 답해야 적힌다(아니면 409). `events` 가 비면 끈다. 새 서명 비밀은 **이
   * 응답에만** 온다.
   */
  app_events_set(
    id: string,
    sub: { url: string | null; events: readonly AppEventType[]; socket: boolean },
  ): Promise<AppEventSub | null> {
    return this.#call('PUT', `/v1/apps/${id}/events`, sub)
  }

  /**
   * 연달아 실패해 멈춘 사건 구독을 다시 켠다(관리자). 적힌 주소가 도전에 다시 맞게
   * 답해야 풀린다(아니면 409 `url_verification_failed`). 비밀은 그대로다.
   */
  app_events_resume(id: string): Promise<void> {
    return this.#call('POST', `/v1/apps/${id}/events/resume`)
  }

  /** 한 앱에 적힌 단축(관리자). */
  app_shortcuts(id: string): Promise<{ shortcuts: Shortcut[] }> {
    return this.#call('GET', `/v1/apps/${id}/shortcuts`)
  }

  /** 한 앱의 단축을 통째로 갈아 끼운다(관리자). 종류마다 다섯까지. */
  app_shortcuts_set(
    id: string,
    shortcuts: readonly Shortcut[],
  ): Promise<{ shortcuts: Shortcut[] }> {
    return this.#call('PUT', `/v1/apps/${id}/shortcuts`, { shortcuts })
  }

  /** 내 단축 메뉴 — 누를 곳이 있는 앱의 것만, 앱 이름·종류·순서대로. */
  shortcuts(): Promise<ShortcutEntry[]> {
    return this.#call('GET', '/v1/shortcuts')
  }

  /**
   * 단축을 부른다. 전역이면 `seq` 없이 부른 자리의 채널만, 글 단축이면 그 글을. 앱은
   * 받은 트리거로 창을 연다 — 창은 `view_opened` 사건으로 온다.
   */
  shortcut_run(app: string, callback_id: string, channel: ChannelId, seq?: Seq): Promise<void> {
    return this.#call('POST', '/v1/shortcuts/run', {
      app,
      callback_id,
      channel,
      seq: seq ?? null,
    })
  }

  app_revoke(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/apps/${id}`)
  }

  installs(): Promise<Install[]> {
    return this.#call('GET', '/v1/installs')
  }

  uninstall(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/installs/${id}`)
  }

  // ── 앱 디렉터리(0077). 목록·요청은 누구나, 분류·허용·통합 설정은 관리자 ──

  /** 조직의 앱 목록. 빈 조건은 싣지 않는다. */
  directory(q?: { query?: string; category?: AppCategory }): Promise<DirectoryEntry[]> {
    const p = new URLSearchParams()
    if (q?.query) p.set('q', q.query)
    if (q?.category) p.set('category', q.category)
    const s = p.toString()
    return this.#call('GET', s === '' ? '/v1/directory' : `/v1/directory?${s}`)
  }

  directory_one(id: string): Promise<DirectoryEntry> {
    return this.#call('GET', `/v1/directory/${id}`)
  }

  directory_listing(id: string, category: AppCategory, summary: string): Promise<void> {
    return this.#call('PUT', `/v1/directory/${id}`, { category, summary })
  }

  directory_allow(id: string, allowed: boolean): Promise<void> {
    return this.#call('PUT', `/v1/directory/${id}/allowed`, { allowed })
  }

  directory_request(id: string, note: string): Promise<void> {
    return this.#call('POST', `/v1/directory/${id}/request`, { note })
  }

  directory_requests(): Promise<InstallRequest[]> {
    return this.#call('GET', '/v1/directory/requests')
  }

  integrations(): Promise<Integration[]> {
    return this.#call('GET', '/v1/integrations')
  }

  /** 1클릭 설정. 돌아오는 `url`(토큰 포함)은 **이 응답에만** 온다. */
  integration_set_up(
    key: string,
    channel: ChannelId,
    name?: string,
  ): Promise<{ id: string; bot: UserId; key: string; url: string }> {
    return this.#call('POST', `/v1/integrations/${key}`, name ? { channel, name } : { channel })
  }

  /** 조직 명단. 이름표는 여기서 온다 — 메시지에는 번호만 실린다. */
  /** 한 사람. 이름만 아는 상대의 자리와 주소를 확인할 때. */
  member_one(who: UserId): Promise<Member> {
    return this.#call('GET', `/v1/members/${who}`)
  }

  /** 서버는 한 쪽에 `MEMBER_PAGE_MAX` 명까지 준다 — 끝까지 이어 받는다. */
  members(): Promise<Member[]> {
    return this.#all_pages('/v1/members', MEMBER_PAGE_MAX)
  }

  channel_members(channel: ChannelId): Promise<UserId[]> {
    return this.#call('GET', `/v1/channels/${channel}/members`)
  }

  /** 누가 켜져 있는가. 밀지 않고 **묻는다** — 명단은 부르는 쪽이 준다. */
  presence(who: readonly UserId[]): Promise<UserId[]> {
    if (who.length === 0) return Promise.resolve([])
    return this.#call('POST', '/v1/presence', { who: who })
  }

  read_state(): Promise<ReadState[]> {
    return this.#call('GET', '/v1/read-state')
  }

  /**
   * 채널의 최근 `count` 줄. 오래된 것부터 정렬돼 온다.
   *
   * `before` 를 주면 그 줄 **앞쪽**으로 거슬러 올라간다 — 위로 스크롤할 때 쓴다.
   */
  latest(channel: ChannelId, count: number, before?: Seq): Promise<Message[]> {
    const before_part = before === undefined ? '' : `&before=${before}`
    return this.#call('GET', `/v1/channels/${channel}/messages?count=${count}${before_part}`)
  }

  /**
   * 올릴 자리를 받는다. 돌아온 `upload_slot` 에 **60초 안에, 선언한 크기 그대로** `PUT`.
   * 바이트는 이 서버를 지나지 않는다.
   */
  upload_begin(
    channel: ChannelId,
    meta: { filename: string; mime: string; bytes: number },
  ): Promise<{ attachment: Attachment; upload_slot: string }> {
    return this.#call('POST', `/v1/channels/${channel}/attachments`, meta)
  }

  attachment(id: string): Promise<Handout> {
    return this.#call('GET', `/v1/attachments/${encodeURIComponent(id)}`)
  }

  remove_attachment(id: string): Promise<void> {
    return this.#call('DELETE', `/v1/attachments/${encodeURIComponent(id)}`)
  }

  /** 링크 카드. 못 펼친 주소는 답에 빠진다 — `url` 로 맞춰 본다. */
  unfurl(urls: readonly string[]): Promise<LinkCard[]> {
    if (urls.length === 0) return Promise.resolve([])
    return this.#call('POST', '/v1/unfurl', { urls: urls })
  }

  /** 스레드 전부(루트 포함, 오름차순). 페이지가 없다 — 스레드는 짧다. */
  thread(channel: ChannelId, root: Seq): Promise<Message[]> {
    return this.#call('GET', `/v1/channels/${channel}/messages/${root}/thread`)
  }

  /** 반응을 붙인다. 이미 있었으면 `changed: false` — 오류가 아니다. 멱등. */
  react(channel: ChannelId, seq: Seq, emoji: string): Promise<{ changed: boolean }> {
    return this.#call(
      'PUT',
      `/v1/channels/${channel}/messages/${seq}/reactions/${encodeURIComponent(emoji)}`,
    )
  }

  unreact(channel: ChannelId, seq: Seq, emoji: string): Promise<{ changed: boolean }> {
    return this.#call(
      'DELETE',
      `/v1/channels/${channel}/messages/${seq}/reactions/${encodeURIComponent(emoji)}`,
    )
  }

  /** 여러 줄의 반응을 한 번에 센다. 반응이 없는 seq 는 답에 빠진다. */
  reaction_count(channel: ChannelId, seqs: readonly Seq[]): Promise<SeqTally[]> {
    if (seqs.length === 0) return Promise.resolve([])
    return this.#call('POST', `/v1/channels/${channel}/reactions`, { seqs: seqs })
  }

  /** 내 글에 투표를 단다. 글은 먼저 보내 두어야 한다(질문이 곧 그 글이다). */
  poll_create(channel: ChannelId, seq: Seq, ask: PollAsk): Promise<Poll> {
    return this.#call('POST', `/v1/channels/${channel}/messages/${seq}/poll`, ask)
  }

  /** 내 표를 `choices` 로 바꾼다(번호는 0부터). 같은 표면 바뀐 게 없다. */
  poll_vote(channel: ChannelId, seq: Seq, choices: readonly number[]): Promise<Poll> {
    return this.#call('PUT', `/v1/channels/${channel}/messages/${seq}/poll/vote`, {
      choices: choices,
    })
  }

  poll_unvote(channel: ChannelId, seq: Seq): Promise<Poll> {
    return this.#call('DELETE', `/v1/channels/${channel}/messages/${seq}/poll/vote`)
  }

  /** 만든 사람만 닫는다. 닫으면 결과가 굳는다. */
  poll_close(channel: ChannelId, seq: Seq): Promise<Poll> {
    return this.#call('POST', `/v1/channels/${channel}/messages/${seq}/poll/close`)
  }

  /** 여러 줄의 투표를 한 번에. 투표가 없는 seq 는 답에 빠진다. */
  poll_count(channel: ChannelId, seqs: readonly Seq[]): Promise<Poll[]> {
    if (seqs.length === 0) return Promise.resolve([])
    return this.#call('POST', `/v1/channels/${channel}/polls`, { seqs: seqs })
  }

  /**
   * 한 줄 쓴다.
   *
   * `client_msg_id` 는 **부르는 쪽이 정해서 재시도 때도 같은 값을 보낸다**. 그래야
   * 응답을 못 받고 다시 보냈을 때 서버가 같은 줄임을 알아보고 `was_resend` 로 답한다.
   * 여기서 만들어 주면 재시도마다 새 값이 되어 같은 말이 두 번 붙는다.
   */
  send(
    channel: ChannelId,
    blocks: Block[],
    client_msg_id: ClientMsgId,
    parent?: Seq,
    /** 답글을 채널에도 띄운다(슬랙 "채널에도 보내기"). `parent` 가 있을 때만 뜻이 있다. */
    broadcast?: boolean,
  ): Promise<Sent> {
    return this.#call('POST', `/v1/channels/${channel}/messages`, {
      blocks: blocks,
      client_msg_id: client_msg_id,
      ...(parent === undefined ? {} : { parent: parent }),
      ...(parent !== undefined && broadcast === true ? { broadcast: true } : {}),
    })
  }

  /**
   * 나중에 보낸다.
   *
   * `due_at` 은 **절대 시각**이다. "3시간 뒤" 를 서버가 받지 않는 이유는 시간대
   * 때문이다 — 달력은 이 브라우저에 있고, 여기서 바꿔 보내야 "내일 아침 9시" 가
   * 그 사람의 아침이 된다.
   */
  schedule(
    channel: ChannelId,
    blocks: Block[],
    client_msg_id: ClientMsgId,
    due_at: string,
    parent?: Seq,
  ): Promise<Scheduled> {
    return this.#call('POST', `/v1/channels/${channel}/schedule`, {
      blocks: blocks,
      client_msg_id: client_msg_id,
      due_at: due_at,
      ...(parent === undefined ? {} : { parent: parent }),
    })
  }

  /** 내가 걸어 둔 예약들. 가까운 때부터. */
  scheduled(): Promise<Scheduled[]> {
    return this.#call('GET', '/v1/scheduled')
  }

  /** 예약을 거둔다. 이미 나갔으면 못 거둔다. */
  unschedule(id: number): Promise<void> {
    return this.#call('DELETE', `/v1/scheduled/${id}`)
  }

  /**
   * 예약을 고친다 — 본문이나 때(빠진 칸은 그대로). 서버는 새로 걸고 옛것을 거두므로 번호가 바뀐다.
   * 멱등 키는 새 행의 것이라 부르는 쪽이 새로 만든다. 그 사이 옛것이 나갔으면 404.
   */
  reschedule(
    id: number,
    change: { blocks?: Block[]; due_at?: string },
    client_msg_id: ClientMsgId,
  ): Promise<Scheduled> {
    return this.#call('PATCH', `/v1/scheduled/${id}`, { ...change, client_msg_id: client_msg_id })
  }

  /** 예약을 기다리지 않고 지금 보낸다. 이미 나갔으면 404. */
  send_scheduled(id: number): Promise<void> {
    return this.#call('POST', `/v1/scheduled/${id}/send`)
  }

  /** 본문을 통째로 바꾼다. 부분 수정은 없다 — 블록 트리에 "일부" 는 없다. */
  edit(channel: ChannelId, seq: Seq, blocks: Block[]): Promise<void> {
    return this.#call('PATCH', `/v1/channels/${channel}/messages/${seq}`, { blocks: blocks })
  }

  /**
   * 링크 카드 하나를 뗀다. 글쓴이만 된다(아니면 404). 고침으로 찍히지 않는다.
   * 주소는 몸통에 싣는다 — 질의 문자열이면 링크가 접근 로그에 남는다.
   */
  hide_card(channel: ChannelId, seq: Seq, url: string): Promise<void> {
    return this.#call('POST', `/v1/channels/${channel}/messages/${seq}/hide-card`, { url: url })
  }

  /** 묘비를 세운다. 행은 남는다 — seq 에 빈틈이 생기면 갭 복구가 망가진다. */
  delete(channel: ChannelId, seq: Seq): Promise<void> {
    return this.#call('DELETE', `/v1/channels/${channel}/messages/${seq}`)
  }

  /** 여기까지 읽었다. 뒤로 가는 값은 서버가 무시하므로 늦게 온 호출이 뱃지를 되살리지 않는다. */
  mark_read(channel: ChannelId, upto: Seq): Promise<void> {
    return this.#call('POST', `/v1/channels/${channel}/read`, { upto: upto })
  }

  /**
   * 이 줄부터 안 읽음으로 둔다. **커서는 뒤로 간다** — 사람이 직접 되돌린 것이라
   * 늦게 온 보고를 막는 규칙(`mark_read`)을 여기서는 쓰지 않는다.
   */
  /**
   * 앱의 글에 달린 단추를 누르거나 고르기를 고른다. 보내는 것은 블록 자리·`action`·고른
   * 값뿐이고, 앱에게 갈 값은 서버가 글에서 다시 읽는다. 앱이 답하지 않으면 409.
   */
  message_action(
    channel: ChannelId,
    seq: Seq,
    block: number,
    action: string,
    value?: string,
  ): Promise<void> {
    return this.#call('POST', `/v1/channels/${channel}/messages/${seq}/actions`, {
      block,
      action,
      value: value ?? null,
    })
  }

  /**
   * 앱이 내 앞에 연(얹은·고친) 창. `view_opened` 사건을 받으면 부른다. `under` 는 이 창이
   * 얹힌 아래 창이다(뿌리면 `null`). 남의 것·닫힌 것은 404.
   */
  view_get(id: number): Promise<{
    id: number
    channel: ChannelId
    view: View
    under: number | null
    hash: string
  }> {
    return this.#call('GET', `/v1/views/${id}`)
  }

  /**
   * 창을 낸다. `values` 는 칸 이름 → 넣은 값. 결과는 앱의 답이 정한다 — 닫힘, 칸별 오류,
   * 또는 바뀐 창.
   */
  view_submit(id: number, values: Record<string, string>): Promise<ViewOutcome> {
    return this.#call('POST', `/v1/views/${id}/submit`, { values })
  }

  /** 이 창을 닫는다. 그 위에 얹힌 창도 함께 닫힌다. */
  view_close(id: number): Promise<void> {
    return this.#call('POST', `/v1/views/${id}/close`)
  }

  mark_unread(channel: ChannelId, from: Seq): Promise<void> {
    return this.#call('POST', `/v1/channels/${channel}/unread`, { from })
  }

  /** 든 채널을 모두 읽음으로. 답은 실제로 움직인 채널 수다. */
  read_all(): Promise<{ moved: number }> {
    return this.#call('POST', '/v1/read-all')
  }

  /** 나를 향한 일들. `before` 는 RFC 3339 시각이고, 그보다 오래된 것만 준다. */
  activity(before?: string, limit?: number): Promise<Activity[]> {
    const q = new URLSearchParams()
    if (before !== undefined) q.set('before', before)
    if (limit !== undefined) q.set('limit', String(limit))
    const tail = q.toString()
    return this.#call('GET', tail === '' ? '/v1/activity' : `/v1/activity?${tail}`)
  }

  /** 갭 백필. 서버는 한 번에 200 까지만 준다(`BACKFILL_CHUNK`) — 쪼개는 건 엔진이 한다. */
  message_range(channel: ChannelId, since: Seq, upto: Seq): Promise<Message[]> {
    return this.#call('GET', `/v1/channels/${channel}/messages?since=${since}&upto=${upto}`)
  }

  /**
   * `?limit=&after=` 쪽을 끝까지 이어 받는다. 다음 시작점은 앞 쪽 마지막 줄의 `id` 다
   * (서버의 `x-next-after` 와 같은 값). 덜 찬 쪽이 끝이다.
   */
  async #all_pages<T extends { id: number }>(route: string, size: number): Promise<T[]> {
    const out: T[] = []
    let after: number | undefined
    let full = true
    while (full) {
      const q = after === undefined ? `limit=${size}` : `limit=${size}&after=${after}`
      // 쪽은 앞 쪽의 마지막 줄을 알아야 물을 수 있다 — 순서대로일 수밖에 없다.
      const page: T[] = await this.#call('GET', `${route}?${q}`)
      out.push(...page)
      after = page.at(-1)?.id
      full = page.length >= size && after !== undefined
    }
    return out
  }

  async #call<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    route: string,
    body?: unknown,
  ): Promise<T> {
    const token = await this.#config.token()
    const head: Record<string, string> = { authorization: `Bearer ${token}` }
    if (body !== undefined) head['content-type'] = 'application/json'

    const send = this.#config.fetch ?? fetch
    const res = await send(`${this.#config.base_url}${route}`, {
      method: method,
      headers: head,
      body: body === undefined ? null : JSON.stringify(body),
      // 토큰이 헤더에 있으므로 쿠키는 필요 없다. 실어 보내면 CSRF 면적만 넓어진다.
      credentials: 'omit',
    })

    if (!res.ok) {
      const again = res.headers.get('retry-after')
      throw new ApiError(
        res.status,
        res.headers.get('x-request-id'),
        again === null ? null : Number.parseInt(again, 10) || null,
        await error_body(res),
      )
    }
    // 201·204 처럼 몸통 없는 성공이 있다(초대·나가기). 빈 몸통을 JSON 으로 읽으면 던진다.
    const text = await res.text()
    return (text.length === 0 ? undefined : JSON.parse(text)) as T
  }
}

/** 바깥 조직과의 이음 한 줄. `peer` 는 상대 조직 번호, `host` 는 이 편이 채널 주인인가. */
export interface ConnectLink {
  channel: ChannelId
  peer: number
  peer_name: string
  host: boolean
  /** 외부 사용자와의 1:1 DM 이음인가. */
  dm?: boolean
}

export interface ConnectPolicy {
  peers_max: number
  dm_allowed: boolean
}

export interface ConnectPerson {
  user: UserId
  name: string
  org: number
}

export interface ConnectDmInvite {
  id: string
  from_tenant: number
  from_user: number
  from_name: string
  from_org: string
  expires_at: string
}

export interface ConnectInvite {
  token: string
  expires_at: string
}

export interface ConnectPending {
  host: number
  host_name: string
  channel_name: string
  expires_at: string
}
